// 용사의 시련 한 판의 흐름(3개월 C7, 데이터·판정은 shared/content/trials.ts, 정본 docs/PROGRESSION-720H.md 12절).
// 입장(enterTrial) → 그 스테이지 판 계획을 custom 경로로 로드(스테이지 진행·첫 클리어·카드·결과 영수증을 만들지 않는다) → 진행(속공 남은 시간·무혈 최저 HP·연격 콤보)
// → 클리어 판정·지급 / 실패(제한 시간·HP 문턱을 넘으면 바로 끝낸다). 시련 판은 경험치가 없다(GameSim.grantXp가 running이면 주지 않는다).
// 지급: 강화석 = 장비 장부 고정 id 연산(`trial:<id>:s`)을 먼저 넣고 장비 저장이 확인되면 골드 = save.v1 보너스 영수증(`trial:<id>`, 지워지지 않는 고정 id —
// 완료 기록을 겸한다)을 적는다. 장비 저장이 막히면 영수증을 적지 않고 메모리에 붙잡아 정산 주기마다 다시 시도한다(받는 쪽이 같은 id를 걸러 정확히 한 번).
// 탭이 닫혀 못 적으면 그 시련은 이룬 것으로 남지 않는다(잃는 쪽으로 닫는다 — 다시 이루면 된다). 상자(10개 모두)도 같은 순서다.
// 알려진 동작(의도 또는 별도 수정 대상):
//  - 시련 판에서도 일반 드롭이 나온다(의도된 동작: 경험치만 0).
//  - 결과 화면에서 「다시 도전」을 누른 뒤 loadMap이 실패하면 lastId가 지워져 재시도 버튼이 먹통이 된다(마을 복귀는 된다). 별도 수정 대상.
//  - 영수증이 pending인 동안 같은 시련을 다시 이루면 신규 보상 표시가 또 나온다(지급은 중복되지 않는다).
// 고정 60Hz step 안에서는 새 객체를 만들지 않는다(문구는 값이 바뀔 때만 만든다).
import { STARS } from '../shared/config';
import { Ev } from '../shared/events';
import { anyStagePlan, parseAnyStage } from '../shared/content/stages';
import { TRIALS, TRIAL_BOARDS, boardTrialsOf, isTrialBoardId, secText, trialBoard, trialChestId, trialDef, trialFailedEarly, trialMet, trialRegionKey, trialRewardId, type TrialBoardId, type TrialDef, type TrialReward, type TrialsView } from '../shared/content/trials';
import { gridOfMap } from '../shared/stageMaps';
import type { RegionId } from '../shared/levels';
import type { StageId } from '../shared/content/types';
import { BossState, HeroAct, Scene, type HeroActId } from '../shared/types';
import { JOB_EVENT_BASE, JOB_GATES, jobGateDef, jobGateRewardId, type JobGateDef } from '../shared/content/jobQuest';
import type { GameSim } from './game';

const TICK_HZ = 60;
/** Ev.TrialEnter c: 거절 이유 */
export const TRIAL_DENY = { scene: 1, busy: 2, locked: 3, unknown: 4, load: 5 } as const;
/** Ev.TrialResult a: 0 실패(판 중 문턱) 1 클리어했지만 조건 미달 2 이룸(처음) 3 이룸(이미 이뤘던 시련) / b: 지급 대기(저장 재시도 중) 1 */
export const TRIAL_OUTCOME = { failed: 0, missed: 1, achieved: 2, again: 3 } as const;

export class TrialRun {
  running = false;
  /** 지금(또는 마지막) 판의 시련. 전직 관문(C10-5, JobGateDef — tier 필드가 있다)도 같은 판 흐름으로 돈다 */
  def: TrialDef | JobGateDef | null = null;
  /** 마지막으로 들어간 시련(결과·패배 화면의 '다시 도전'). 다른 판에 들어가면 비운다 */
  lastId = '';
  private hpLow = 100;
  /** 마지막 적을 쓰러뜨린 판 시각(틱, 클리어 연출 대기 전). 속공 판정은 이 시각으로 한다(-1 = 아직) */
  private killTick = -1;
  /** 판 이름(입장 때 한 번 만든다) */
  private label = '';
  private lastKey = -1;
  /** 장비 저장이 막혀 아직 영수증을 못 적은 지급(시련 id 또는 상자 id) */
  private readonly pending: string[] = [];
  private readonly doneList: string[] = [];
  private readonly chestList: string[] = [];
  private readonly openList: string[] = [];
  private readonly liveState = { id: '', status: '', danger: false };
  private revNo = 0;
  readonly view: TrialsView;

  constructor(private readonly g: GameSim) {
    const self = this;
    this.view = {
      get done() { return self.doneList; }, get chests() { return self.chestList; }, get open() { return self.openList; },
      get live() { return self.liveState; }, get rev() { return self.revNo; },
    };
  }

  /** 시련 판 결과·패배 화면인가(다시 도전 버튼이 시련으로 다시 들어간다) */
  get showingResult(): boolean {
    const s = this.g.flow.scene;
    return !this.running && this.lastId !== '' && (s === Scene.Result || s === Scene.Defeat);
  }
  get payoutPending(): boolean { return this.pending.length > 0; }

  isDone(id: string): boolean { return this.g.save.hasResult(trialRewardId(id)); }
  chestDone(board: TrialBoardId): boolean { return this.g.save.hasResult(trialChestId(board)); }
  /** 그 스테이지를 한 번이라도 깼다(이관 계정은 깬 수로 본다 — 이정표 milestoneClears와 같은 규칙) */
  isOpen(t: TrialDef): boolean {
    const p = parseAnyStage(t.stage);
    if (!p) return false;
    const ord = (p.region - 1) * 10 + p.index;
    if (p.diff === 'hard') return this.g.hard.stageClears(t.stage) > 0 || ord <= this.g.hard.cleared;
    if (p.diff === 'hell') return this.g.hell.stageClears(t.stage) > 0 || ord <= this.g.hell.cleared;
    return this.g.progress.stageClears(t.stage as StageId) > 0 || ord <= this.g.progress.cleared;
  }

  /** 이룬·열린 목록을 저장소 기준으로 다시 만든다(마을 진입·지급 뒤). 고정 스텝 밖에서만 부른다 */
  refresh(): void {
    const d: string[] = [], c: string[] = [], o: string[] = [];
    for (const t of TRIALS) { if (this.isDone(t.id)) d.push(t.id); if (this.isOpen(t)) o.push(t.id); }
    for (const b of TRIAL_BOARDS) if (this.chestDone(b.id)) c.push(b.id);
    const same = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x, i) => x === b[i]);
    if (same(d, this.doneList) && same(c, this.chestList) && same(o, this.openList)) return;
    this.doneList.length = 0; this.doneList.push(...d);
    this.chestList.length = 0; this.chestList.push(...c);
    this.openList.length = 0; this.openList.push(...o);
    this.revNo++;
  }

  private emitEnter(ok: number, code: number): void {
    const e = this.g.emit(Ev.TrialEnter); e.a = ok; e.b = this.def ? eventNo(this.def) : -1; e.c = code;
  }

  /** 입장 명령. 마을(이동·일시정지 아님), 또는 시련 판의 결과·패배 화면(다시 도전)에서만 */
  enter(id: string): void {
    const g = this.g, f = g.flow, def: TrialDef | JobGateDef | null = trialDef(id) ?? jobGateDef(id);
    if (!def) { this.emitEnter(0, TRIAL_DENY.unknown); return; }
    const sceneOk = (f.scene === Scene.Village && !f.paused && f.travelTo === 0 && !g.village.open) || (this.showingResult && !f.paused);
    if (!sceneOk) { this.emitEnter(0, TRIAL_DENY.scene); return; }
    // 파티·레이드 중에는 쓰러져도 trialDefeat가 불리지 않아 running이 남을 수 있다(리뷰 P2): 입장을 막는다
    if (g.party.active || g.raidNet.active) { this.emitEnter(0, TRIAL_DENY.busy); return; }
    // 카드를 아직 안 고른 결과가 저장돼 있으면 loadMap이 그 결과를 되살린다: 고른 뒤에 들어온다
    if (g.save.unpickedResult()) { this.emitEnter(0, TRIAL_DENY.busy); return; }
    if (isJobGate(def) ? !g.jobRun.gateOpen(def) : !this.isOpen(def)) { this.def = def; this.emitEnter(0, TRIAL_DENY.locked); this.def = null; return; }
    const plan = anyStagePlan(def.stage);
    const label = isJobGate(def) ? def.name : '시련 · ' + def.name;
    g.flowController.loadMap(plan.region, null, { scene: Scene.Dungeon, plan, map: plan.map, grid: gridOfMap(plan.map), label, objective: '첫 전투방으로 이동하세요.' });
    if (f.scene !== Scene.Dungeon || g.flowController.plan !== plan) { this.emitEnter(0, TRIAL_DENY.load); return; }
    this.running = true; this.def = def; this.lastId = def.id; this.hpLow = 100; this.killTick = -1; this.lastKey = -1; this.label = label;
    // custom 경로는 판 종류를 'field'로 둔다: 스테이지 판 종류(보스 HUD·아레나 문구)로 되돌린다. 진행 기록은 stageClear가 시련이면 건너뛴다
    f.stageKind = plan.kind; f.stageLabel = label;
    this.liveState.id = def.id; this.setStatus();
    this.emitEnter(1, 0);
  }

  /** 지금 판의 진행 문구(1초에 한 번·값이 바뀔 때만 새 문자열) */
  private setStatus(): void {
    const def = this.def, f = this.g.flow;
    if (!def) return;
    const sec = Math.floor(f.elapsedTicks / TICK_HZ);
    let key: number, text: string, danger = false;
    if (def.kind === 'speed') {
      const left = Math.max(0, def.target - sec);
      key = left; danger = left <= 15;
      if (key === this.lastKey) return;
      text = '속공 · 남은 ' + secText(left);
    } else if (def.kind === 'guard') {
      key = Math.floor(this.hpLow); danger = this.hpLow < def.target + 10;
      if (key === this.lastKey) return;
      text = '무혈 · 최저 HP ' + key + '% (' + def.target + '% 이상)';
    } else if (def.kind === 'combo') {
      const best = Math.min(def.target, this.g.combo.best);
      key = best;
      if (key === this.lastKey) return;
      text = '연격 · 최고 ' + best + ' / ' + def.target + ' 콤보';
    } else {
      key = 0;
      if (key === this.lastKey) return;
      text = '완벽한 승리 · 5분 안에, HP 50% 이상으로';
    }
    this.lastKey = key;
    // 전직 관문(C10-5)이면 진행 띠 앞에 「전직」을 붙여 시련과 구분한다(관문 이름은 스테이지 라벨에 나온다)
    if (isJobGate(def)) text = '전직 관문 · ' + text;
    this.liveState.status = text; this.liveState.danger = danger;
    this.revNo++;
  }

  /** 고정 스텝 1회(FlowController.step 끝). 문턱을 넘으면 바로 실패 */
  step(): void {
    if (!this.running || !this.def) return;
    const g = this.g, f = g.flow, h = g.hero;
    if (f.scene !== Scene.Dungeon) return;
    if (f.stageLabel !== this.label) f.stageLabel = this.label;
    this.sampleHp();
    // 마지막 적을 잡은 뒤(클리어 연출 대기)에는 실패로 치지 않고, 그 시각을 속공 판정에 쓴다
    const bs = g.boss.state;
    const done = f.clearHold > 0 || g.flowController.finalKill() || (f.roomIndex === g.flowController.plan.rooms.length - 1 && (bs === BossState.Dying || bs === BossState.Dead));
    if (done && this.killTick < 0) this.killTick = f.elapsedTicks;
    if (!done && trialFailedEarly(this.def, f.elapsedTicks / TICK_HZ, this.hpLow)) { this.fail(this.def.kind === 'speed' ? '시간 초과!' : 'HP가 문턱 밑으로 떨어졌어요!'); return; }
    this.setStatus();
  }

  /** 최저 HP 기록(GameSim.step이 히트스톱·물약보다 먼저 매 틱 부른다 — 피해 직후 회복으로 문턱 넘김이 지워지지 않게) */
  sampleHp(): void {
    if (!this.running) return;
    const h = this.g.hero;
    if (h.maxHp > 0) { const pct = (h.hp / h.maxHp) * 100; if (pct < this.hpLow) this.hpLow = pct; }
  }

  /** 클리어 정산(FlowController.stageClear가 시련 판이면 부른다). 같은 판을 두 번 불러도 한 번만 지급된다 */
  trialClear(): void {
    const g = this.g, f = g.flow, r = g.result, h = g.hero, def = this.def;
    if (!this.running || !def || f.scene !== Scene.Dungeon) return;
    this.running = false;
    if (f.roomIndex >= 0) f.roomState[f.roomIndex] = 2;
    f.objectiveKilled = f.objectiveTotal;
    g.emit(Ev.RoomClear).a = f.roomIndex;
    g.emit(Ev.StageClear);
    r.cleared = true; r.clearTicks = f.elapsedTicks; r.maxCombo = g.combo.best;
    r.starMask = 1 | (r.clearTicks <= STARS.timeTicks ? 2 : 0) | (h.hp >= h.maxHp * STARS.healthRatio ? 4 : 0);
    r.stars = (r.starMask & 1) + ((r.starMask >> 1) & 1) + ((r.starMask >> 2) & 1);
    r.cards.fill(0); r.pickedCard = 0; r.stageId = ''; r.nextStage = '';
    this.sampleHp();
    // 속공: 클리어 연출(필수 대기)이 끝난 시각이 아니라 마지막 적을 쓰러뜨린 시각으로 잰다(PR #293 Codex P2)
    const killSec = (this.killTick >= 0 ? Math.min(this.killTick, r.clearTicks) : r.clearTicks) / TICK_HZ;
    const met = trialMet(def, { cleared: true, clearSec: killSec, maxCombo: r.maxCombo, hpLowPct: this.hpLow, stars: r.stars });
    const job = isJobGate(def);
    const before = job ? g.jobRun.passed(def.id) : this.isDone(def.id);
    r.trialId = def.id; r.trialMet = met; r.trialNew = met && !before; r.trialLow = Math.floor(this.hpLow);
    r.gold = 0; r.trialStones = 0; r.trialStoneTier = 0; r.trialChest = ''; r.jobReady = 0;
    if (r.trialNew && job) {
      this.pay(jobGateRewardId(def.id), def.reward, `r${def.region}`);
      r.gold = def.reward.gold; r.trialStones = def.reward.stones; r.trialStoneTier = def.reward.tier;
    } else if (r.trialNew && !job) {
      this.pay(trialRewardId(def.id), def.reward, trialRegionKey(trialBoard(def.board)));
      r.gold = def.reward.gold; r.trialStones = def.reward.stones; r.trialStoneTier = def.reward.tier;
      if (boardTrialsOf(def.board).every((t) => this.isDone(t.id) || this.pending.includes(trialRewardId(t.id)))) r.trialChest = def.board;
    }
    r.resultId = 'trialrun:' + def.id + ':' + g.tick;
    g.growth.commitRun();
    g.loot.sweep();
    g.gear.flush(true);
    this.refresh();
    if (job) { g.jobRun.refresh(); r.jobReady = g.jobRun.view.ready; }
    r.savePending = g.save.savePending || this.payoutPending;
    r.saveRecovered = g.save.recovered > 0;
    g.profile.sync();
    f.scene = Scene.Result; f.sceneTick = 0; f.paused = false;
    f.objectiveLabel = !met ? (job ? '관문 조건을 못 채웠어요. 다시 도전하거나 마을로 돌아가세요.' : '시련 조건을 못 채웠어요. 다시 도전하거나 마을로 돌아가세요.')
      : job ? (r.jobReady ? '관문을 모두 넘었어요! 마을 교관 세르에게 가서 ' + (r.jobReady === 1 ? '갈래를 고르세요.' : '전직 II를 받으세요.') : r.trialNew ? '관문을 넘었어요! 다음 관문이 열렸어요.' : '이미 넘은 관문이에요. 기록만 남아요.')
      : r.trialNew ? (r.trialChest ? '시련판을 모두 이뤘어요! 마을 퀘스트 창 「시련」에서 상자를 여세요.' : '시련을 이뤘어요! 보상이 들어갔어요.')
      : '이미 이룬 시련이에요. 기록만 남아요.';
    const e = g.emit(Ev.TrialResult); e.a = !met ? TRIAL_OUTCOME.missed : r.trialNew ? TRIAL_OUTCOME.achieved : TRIAL_OUTCOME.again; e.b = this.payoutPending ? 1 : 0; e.c = eventNo(def);
    this.endRun(HeroAct.Victory);
    g.emit(Ev.SceneChange).a = Scene.Result;
  }

  /** 문턱 실패(속공 시간 초과·무혈 HP): 부활 깃털 없이 바로 패배 장면 */
  private fail(why: string): void {
    const g = this.g, f = g.flow, def = this.def;
    if (!this.running || f.scene !== Scene.Dungeon || !def) return;
    this.running = false;
    f.scene = Scene.Defeat; f.sceneTick = 0; f.paused = false;
    f.objectiveLabel = why + (isJobGate(def) ? ' 관문 실패' : ' 시련 실패') + ' — 다시 도전하거나 마을로 돌아가세요.';
    g.result.cleared = false; g.result.clearTicks = f.elapsedTicks; g.result.maxCombo = g.combo.best;
    g.result.trialId = def.id; g.result.trialMet = false; g.result.trialNew = false; g.result.trialLow = Math.floor(this.hpLow);
    g.growth.commitRun();
    g.gear.flush(true);
    this.endRun(HeroAct.Idle);
    const e = g.emit(Ev.TrialResult); e.a = TRIAL_OUTCOME.failed; e.b = 0; e.c = eventNo(def);
    g.emit(Ev.Defeat);
    g.emit(Ev.SceneChange).a = Scene.Defeat;
  }

  private endRun(act: HeroActId): void {
    const g = this.g, h = g.hero;
    h.act = act; h.actTick = 0; h.invulnerable = false; h.y = 0;
    g.hitstop = 0;
    g.awakening.cancel();
    g.mobController.reset();
    g.releaseAllTelegraphs();
    g.skillFx.clear();
    g.arrows.clear();
    this.liveState.id = ''; this.liveState.status = ''; this.liveState.danger = false; this.revNo++;
  }

  /** 영웅이 쓰러짐(FlowController.defeat가 Defeat 장면으로 바꾼 직후) */
  trialDefeat(): void {
    if (!this.running || !this.def) return;
    const r = this.g.result, def = this.def;
    this.running = false;
    r.trialId = def.id; r.trialMet = false; r.trialNew = false; r.trialLow = 0;
    this.g.flow.objectiveLabel = '쓰러졌어요. ' + (isJobGate(def) ? '관문' : '시련') + ' 실패 — 다시 도전하거나 마을로 돌아가세요.';
    this.liveState.id = ''; this.liveState.status = ''; this.revNo++;
    const e = this.g.emit(Ev.TrialResult); e.a = TRIAL_OUTCOME.failed; e.b = 0; e.c = eventNo(def);
  }

  /** 판 도중 마을·다른 판으로 떠남(loadMap) */
  abandon(): void {
    this.running = false;
    this.liveState.id = ''; this.liveState.status = '';
  }
  /** 다른 판(시련 아님)에 들어갔다: 결과 화면의 '다시 도전'이 시련을 잇지 않게 */
  forget(): void { if (!this.running) { this.lastId = ''; this.def = null; } }

  /** 시련 상자 열기(마을 퀘스트 창). 10개를 모두 이뤘고 아직 안 받았을 때만 */
  claimChest(board: TrialBoardId): boolean {
    const g = this.g;
    if (g.flow.scene !== Scene.Village) return false;
    if (!isTrialBoardId(board)) return false;
    const b = trialBoard(board);
    if (this.chestDone(board) || !boardTrialsOf(board).every((t) => this.isDone(t.id))) return false;
    this.pay(trialChestId(board), b.chest, trialRegionKey(b));
    this.refresh();
    g.profile.sync();
    const e = g.emit(Ev.TrialChest); e.a = TRIAL_BOARDS.indexOf(b); e.b = this.payoutPending ? 1 : 0; e.c = b.chest.gold; e.d = b.chest.stones;
    return true;
  }

  /** 강화석(장비 장부) → 저장 확인 → 골드 영수증(완료 기록). 장비 저장이 막히면 붙잡아 둔다 */
  private pay(id: string, rw: TrialReward, region: RegionId): void {
    const g = this.g;
    const at = this.pending.indexOf(id);
    // 이미 영수증이 있다(이 탭 또는 다른 탭이 지급을 끝냄): 붙잡아 둔 것도 푼다(PR #293 Codex P2)
    if (g.save.hasResult(id)) { if (at >= 0) this.pending.splice(at, 1); return; }
    if (rw.stones > 0) {
      const res = g.gear.submit({ id: id + ':s', t: 'stones', tier: rw.tier, n: rw.stones });
      if ((res.code !== 'ok' && res.code !== 'duplicate') || !g.gear.flush(true)) { if (!this.pending.includes(id)) this.pending.push(id); return; }
    }
    g.save.recordResult({ id, region, baseGold: rw.gold, clearTicks: 0, maxCombo: 0, stars: 0, starMask: 0, kills: 0, damageDealt: 0, cards: [0, 0, 0], pickedCard: 0, bonus: true });
    const i = this.pending.indexOf(id);
    if (i >= 0) this.pending.splice(i, 1);
  }

  /** 붙잡아 둔 지급 다시 시도(FlowController 정산 주기, 1초) */
  retryPending(): void {
    if (this.pending.length === 0) return;
    for (const id of this.pending.slice()) {
      const t = TRIALS.find((x) => trialRewardId(x.id) === id);
      if (t) { this.pay(id, t.reward, trialRegionKey(trialBoard(t.board))); continue; }
      const b = TRIAL_BOARDS.find((x) => trialChestId(x.id) === id);
      if (b) { this.pay(id, b.chest, trialRegionKey(b)); continue; }
      const j = JOB_GATES.find((x) => jobGateRewardId(x.id) === id);
      if (j) this.pay(id, j.reward, `r${j.region}`);
    }
    if (this.pending.length === 0) { this.refresh(); this.g.jobRun.refresh(); this.g.result.savePending = this.g.save.savePending; }
  }
}

/** 전직 관문인가(C10-5) */
function isJobGate(d: TrialDef | JobGateDef): d is JobGateDef { return 'tier' in d; }
/** 이벤트 순번: 시련 = TRIALS 순번, 전직 관문 = JOB_EVENT_BASE + JOB_GATES 순번 */
function eventNo(d: TrialDef | JobGateDef): number { return isJobGate(d) ? JOB_EVENT_BASE + JOB_GATES.indexOf(d) : TRIALS.indexOf(d); }
